import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!token) {
            setError("This reset link is missing its token.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            if (data.success) setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="reset-password-page">
            <div className="border-2 border-ink p-8">
                {done ? (
                    <div className="text-center py-6" data-testid="reset-success">
                        <CheckCircle2 className="text-pos mx-auto" size={40} />
                        <h1 className="font-serif text-3xl font-black mt-4">Password updated</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Your password has been reset successfully.</p>
                        <button onClick={() => navigate("/login")} data-testid="reset-goto-login"
                            className="mt-6 w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150">
                            Go to log in
                        </button>
                    </div>
                ) : (
                    <>
                        <h1 className="font-serif text-3xl font-black">Set a new password</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Choose a new password for your account.</p>
                        <form onSubmit={submit} className="mt-8 space-y-5">
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">New password</label>
                                <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password"
                                    className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                            </div>
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Confirm password</label>
                                <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm"
                                    className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                            </div>
                            {error && <p className="text-sm text-neg border border-neg p-3" data-testid="reset-error">{error}</p>}
                            <button type="submit" disabled={loading} data-testid="reset-submit"
                                className="w-full bg-accent text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink transition-colors duration-150 disabled:opacity-50">
                                {loading ? "Updating…" : "Reset password"}
                            </button>
                        </form>
                        <p className="mt-6 text-sm text-muted-foreground">
                            <Link to="/login" className="text-accent link-underline">Back to log in</Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
